import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Calendar, X, Phone } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { trackEvent } from "@/lib/analytics";

/**
 * Floating action bar.
 *
 * Mobile  : full-width 3-section bar pinned to the bottom of the viewport.
 * Desktop : compact 3-section bar pinned to the bottom-right corner.
 *
 * Order in both cases: Call Now (grey) · WhatsApp (green) · Book a Survey (red)
 * — WhatsApp in the centre as the primary action.
 *
 * Uses an extreme z-index so it always sits above any platform badge.
 */
const WHATSAPP_PHONE = "447538243578"; // E.164 without leading +
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Hi RetrofitIQ — I'd like to ask about a building performance survey."
);

export default function FloatingActions() {
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("riq:fab:dismissed") === "1";
  });

  if (hidden) return null;

  const onDismiss = () => {
    sessionStorage.setItem("riq:fab:dismissed", "1");
    setHidden(true);
  };

  // Action buttons rendered identically on mobile + desktop; only the wrapping
  // container changes (full-width grid on mobile, compact flex on desktop).
  const callBtn = (
    <a
      href={BUSINESS.phoneHref}
      data-testid="fab-call"
      onClick={() => trackEvent("contact_cta_click", { cta: "call", placement: "fab" })}
      className="flex-1 h-11 md:h-10 flex items-center justify-center gap-1.5 bg-slate-700 hover:bg-slate-800 active:scale-[0.98] text-white rounded-xl shadow-2xl shadow-black/40 ring-2 ring-white transition-transform"
    >
      <Phone className="w-[18px] h-[18px] md:w-4 md:h-4 shrink-0" />
      <span className="text-[13px] md:text-[11px] font-bold uppercase tracking-[0.06em] leading-none">Call Now</span>
    </a>
  );

  const whatsappBtn = (
    <a
      href={`https://wa.me/${WHATSAPP_PHONE}?text=${WHATSAPP_MESSAGE}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Message ${BUSINESS.name} on WhatsApp`}
      data-testid="fab-whatsapp"
      onClick={() => trackEvent("contact_cta_click", { cta: "whatsapp", placement: "fab" })}
      className="flex-1 h-11 md:h-10 flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe5d] active:scale-[0.98] text-white rounded-xl shadow-2xl shadow-black/40 ring-2 ring-white transition-transform"
    >
      <MessageCircle className="w-[18px] h-[18px] md:w-4 md:h-4 shrink-0" fill="currentColor" />
      <span className="text-[13px] md:text-[11px] font-bold uppercase tracking-[0.06em] leading-none">WhatsApp</span>
    </a>
  );

  const bookBtn = (
    <Link
      to="/contact"
      data-testid="fab-consult"
      onClick={() => { trackEvent("contact_cta_click", { cta: "book_survey", placement: "fab" }); trackEvent("book_survey_click", { location: "floating_action", link_text: "Book Survey" }); }}
      className="flex-1 h-11 md:h-10 flex items-center justify-center gap-1.5 bg-[#DC2626] hover:bg-[#b91c1c] active:scale-[0.98] text-white rounded-xl shadow-2xl shadow-black/40 ring-2 ring-white transition-transform"
    >
      <Calendar className="w-[18px] h-[18px] md:w-4 md:h-4 shrink-0" />
      <span className="text-[13px] md:text-[11px] font-bold uppercase tracking-[0.06em] leading-none">Book Survey</span>
    </Link>
  );

  return (
    <>
      {/* Mobile bar — full width, slimmer height (44 px), larger icons & labels. */}
      <div
        className="md:hidden fixed z-[2147483646] left-2 right-2 bottom-2 flex gap-2"
        role="region"
        aria-label="Quick contact"
        data-testid="floating-actions"
      >
        {callBtn}
        {whatsappBtn}
        {bookBtn}
      </div>

      {/* Desktop bar — compact, bottom-right, approximately the previous
          WhatsApp pill width (~360 px). Three equal buttons, WhatsApp centred. */}
      <div
        className="hidden md:flex fixed z-[2147483646] right-6 bottom-6 w-[360px] gap-2"
        role="region"
        aria-label="Quick contact"
        data-testid="floating-actions-desktop"
      >
        {callBtn}
        {whatsappBtn}
        {bookBtn}

        {/* Tiny dismiss handle — hidden by default, reveals on hover/focus. */}
        <button
          type="button"
          onClick={onDismiss}
          data-testid="fab-dismiss"
          aria-label="Dismiss quick-contact buttons"
          className="absolute -top-2 -right-2 w-5 h-5 grid place-items-center bg-slate-900 text-white rounded-full opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </>
  );
}
